import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule, inject, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { BqxApplicationAbstractService, BqxApplicationModule, BqxApplicationService } from 'bqx-application';
import {
  BpSdkApplicationFormAbstractService,
  BpSdkApplicationFormService,
  BqxApplicationWebComponentsModule
} from 'bqx-application-web-components';
import { BqxAuthClientAbstractService, BqxAuthClientService, BqxAuthModule } from 'bqx-auth';
import { BqxBusinessTypeComponentModule } from 'bqx-businesstype-component';
import { BqxBusinesstypeRequestsModule, BusinessTypeRequestsAbstractService, BusinessTypeRequestsService } from 'bqx-businesstype-requests';
import { AbstractDealConfigService, BqxDealModule, DealAbstractService, DealService } from 'bqx-deal';
import { AbstractFlagrService, BqxFeatureSwitchesModule, FlagrService } from 'bqx-feature-switches';
import { BqxLoggingModule, HttpErrorInterceptor, LoggerAbstractService, LoggerService, ReportingErrorHandler } from 'bqx-logging';
import { BQXConfiguration, BqxUiCommonModule } from 'bqx-ui-common';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { NgxMaskDirective, NgxMaskPipe, provideEnvironmentNgxMask } from 'ngx-mask';
import { environment } from 'src/environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppConfig } from './app.config';
import { AuthGuard } from './auth-guard.guard';
import { BusinessTypeQuestionsComponent } from './components/application-form/business-type-questions/business-type-questions.component';
import { ContactInfoQuestionsComponent } from './components/application-form/contact-info-questions/contact-info-questions.component';
import { DropdownInputComponent } from './components/application-form/input-controls/dropdown-input/dropdown-input.component';
import { GoogleAddressInputComponent } from './components/application-form/input-controls/google-address-input/google-address-input.component';
import { QuestionSetSaveButtonComponent } from './components/application-form/input-controls/question-set-save-button/question-set-save-button.component';
import { TextInputComponent } from './components/application-form/input-controls/text-input/text-input.component';
import { ProductInfoQuestionsComponent } from './components/application-form/product-info-questions/product-info-questions.component';
import { QuestionSetContainerComponent } from './components/application-form/question-set-container/question-set-container.component';
import { QuestionSetNavigationComponent } from './components/application-form/question-set-navigation/question-set-navigation.component';
import { QuestionSetComponent } from './components/application-form/question-set/question-set.component';
import { QuoteInfoComponent } from './components/application-form/quote-info/quote-info.component';
import { SdkQuestionSetComponent } from './components/application-form/sdk-question-set/sdk-question-set.component';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { HeaderComponent } from './components/header/header.component';
import { HomePageComponent } from './components/home-page/home-page.component';
import { SearchDetailComponent } from './components/search-application-forms/search-detail/search-detail.component';
import { SearchHeaderComponent } from './components/search-application-forms/search-header/search-header.component';
import { SearchApplicationFormsComponent } from './components/search-application-forms/search-view/search-application-forms.component';
import { ApplicationFormHeaderComponent } from './components/shared/application-form-header/application-form-header.component';
import { QuickGuideComponent } from './components/shared/quick-guide/quick-guide.component';
import { LogoutComponent } from './components/un-authorized-pages/logout/logout.component';
import { ApplicationFormAbstractService } from './services/application-form/application-form.abstract.service';
import { ApplicationFormService } from './services/application-form/application-form.service';
import { AbstractAuthService } from './services/auth/auth.abstract.service';
import { AuthService, authAccessToken } from './services/auth/auth.service';
import { PingAuthService } from './services/auth/ping-auth.service';
import { AbstractDealAnalyticsService } from './services/deal-analytics/abstract-deal-analytics.service';
import { DealAnalyticsService } from './services/deal-analytics/deal-analytics.service';
import { DealIntegrationConfigService } from './services/deal-integration/deal-integration.service';
import { FormDataAbstractService } from './services/form-data/form-data.abstract.service';
import { FormDataService } from './services/form-data/form-data.service';
import { AbstractGoogleMapApiService } from './services/google-maps/abstract-google-maps.service';
import { GoogleMapApiService } from './services/google-maps/google-maps-service.service';
import { InputFocusAbstractService } from './services/input-focus/input-focus.abstract.service';
import { InputFocusService } from './services/input-focus/input-focus.service';
import { SecondaryDataAbstractService } from './services/secondary-data/secondary-data.abstract.service';
import { SecondaryDataService } from './services/secondary-data/secondary-data.service';
export function initializeApp(appConfig: AppConfig) {
  return () => appConfig.load();
}

export function authServiceFactory(
  authClientService: BqxAuthClientAbstractService,
  bqxApplication: BqxApplicationAbstractService,
  http: HttpClient,
  loggerService: LoggerAbstractService,
): AbstractAuthService {
  if (BQXConfiguration.data?.authProvider === 'PING') {
    return new PingAuthService(authClientService, bqxApplication, http, loggerService);
  }
  return new AuthService(authClientService, bqxApplication);
}

@NgModule({
  declarations: [
    AppComponent,
    DashboardComponent,
    HeaderComponent,
    HomePageComponent,
    QuestionSetContainerComponent,
    QuestionSetComponent,
    SearchApplicationFormsComponent,
    LogoutComponent,
    QuestionSetNavigationComponent,
    QuoteInfoComponent,
    QuestionSetComponent,
    ApplicationFormHeaderComponent,
    BusinessTypeQuestionsComponent,
    QuestionSetSaveButtonComponent,
    ContactInfoQuestionsComponent,
    ProductInfoQuestionsComponent,
    TextInputComponent,
    DropdownInputComponent,
    SdkQuestionSetComponent,
    GoogleAddressInputComponent,
    QuickGuideComponent,
    SearchHeaderComponent,
    SearchDetailComponent
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent], imports: [BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    CommonModule,
    NgbModule,
    ReactiveFormsModule,
    AppRoutingModule,
    BqxUiCommonModule,
    BqxAuthModule,
    MatMenuModule,
    MatToolbarModule,
    MatIconModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatStepperModule,
    BqxBusinesstypeRequestsModule,
    BqxBusinessTypeComponentModule,
    BqxApplicationModule,
    BqxDealModule,
    BqxApplicationWebComponentsModule.forRoot(),
    BqxFeatureSwitchesModule.forRoot(authAccessToken.asObservable()),
    BqxLoggingModule.forRoot(authAccessToken),
    LoggerModule.forRoot({
      serverLoggingUrl: '/api/logs',
      level: !environment.production ? NgxLoggerLevel.DEBUG : NgxLoggerLevel.ERROR,
      serverLogLevel: NgxLoggerLevel.OFF,
      httpResponseType: 'json'
    }),
    NgxMaskDirective,
    NgxMaskPipe], providers: [
      AppConfig,
      AuthGuard,
      BQXConfiguration,
      provideEnvironmentNgxMask(),
      provideAppInitializer(() => {
        const initializerFn = (initializeApp)(inject(AppConfig));
        return initializerFn();
      }),
      { provide: BqxAuthClientAbstractService, useClass: BqxAuthClientService },
      { provide: AbstractAuthService, useFactory: authServiceFactory, deps: [BqxAuthClientAbstractService, BqxApplicationAbstractService, HttpClient, LoggerAbstractService] },
      { provide: ApplicationFormAbstractService, useClass: ApplicationFormService },
      { provide: BusinessTypeRequestsAbstractService, useClass: BusinessTypeRequestsService },
      { provide: InputFocusAbstractService, useClass: InputFocusService },
      { provide: BpSdkApplicationFormAbstractService, useClass: BpSdkApplicationFormService },
      { provide: BqxApplicationAbstractService, useClass: BqxApplicationService },
      { provide: AbstractGoogleMapApiService, useClass: GoogleMapApiService },
      { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
      { provide: LoggerAbstractService, useClass: LoggerService },
      { provide: ErrorHandler, useClass: ReportingErrorHandler },
      { provide: SecondaryDataAbstractService, useClass: SecondaryDataService },
      { provide: FormDataAbstractService, useClass: FormDataService },
      { provide: DealAbstractService, useClass: DealService },
      { provide: AbstractFlagrService, useClass: FlagrService },
      { provide: AbstractDealConfigService, useClass: DealIntegrationConfigService },
      { provide: AbstractDealAnalyticsService, useClass: DealAnalyticsService },
      provideHttpClient(withInterceptorsFromDi())
    ]
})
export class AppModule { }
